/* ==========================================================================
   dtc-header.js — SHOP dropdown, search panel, optional predictive results
   Vanilla. No libraries. Registered through the DTC section lifecycle so the
   theme editor can mount/unmount the header cleanly.

   Behaviour
     - SHOP / SEARCH triggers toggle their panel; only one is ever open.
     - Closes on: Esc (focus returns to the trigger), outside click, focus
       leaving the header, any click inside the SHOP panel, any link click.
     - On phones the panel is a fixed full-height drawer; --hdr-bottom is
       written on open/resize so it sits exactly under the header.
     - Predictive search uses /search/suggest.json. If it fails, the plain
       GET form to /search still works — the form is never JS-dependent.
   ========================================================================== */
(function () {
  'use strict';

  var DTC = window.DTC;
  if (!DTC || !DTC.register) return;

  DTC.register('header', {
    init: function (root) {
      var offs = [];
      var timers = [];
      var openName = null;
      var lastTrigger = null;
      var mqDrawer = window.matchMedia('(max-width: 799px)');

      var triggers = {};
      var panels = {};
      Array.prototype.forEach.call(root.querySelectorAll('[data-dtc-toggle]'), function (btn) {
        triggers[btn.getAttribute('data-dtc-toggle')] = btn;
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-dtc-panel]'), function (p) {
        panels[p.getAttribute('data-dtc-panel')] = p;
      });

      /* --- Mobile drawer: contain focus ---------------------------------- *
       * Under 800px the panel covers the whole viewport, so everything behind
       * it must leave the tab order and the a11y tree entirely. `inert` does
       * both in modern browsers; aria-hidden is the fallback for the rest.
       * ------------------------------------------------------------------ */
      var inerted = [];

      function siblingLandmarks() {
        var sel = 'main, .dtc-footer, .dtc-sec-footer-wrap, .dtc-announce, .dtc-starnav, #MainContent';
        return Array.prototype.filter.call(document.querySelectorAll(sel), function (el) {
          return !root.contains(el) && !el.contains(root);
        });
      }

      function inertBackground() {
        if (inerted.length) return;
        siblingLandmarks().forEach(function (el) {
          inerted.push(el);
          el.inert = true;
          el.setAttribute('aria-hidden', 'true');
        });
      }

      function releaseBackground() {
        inerted.forEach(function (el) {
          el.inert = false;
          el.removeAttribute('aria-hidden');
        });
        inerted = [];
      }

      /* ---------------------------------------------------------------- */
      function positionDrawer() {
        if (!mqDrawer.matches) {
          root.style.removeProperty('--hdr-bottom');
          return;
        }
        root.style.setProperty('--hdr-bottom', Math.max(0, root.getBoundingClientRect().bottom) + 'px');
      }

      function open(name) {
        if (openName === name) return;
        if (openName) close(false);
        var panel = panels[name];
        var btn = triggers[name];
        if (!panel) return;

        lastTrigger = btn || null;
        positionDrawer();
        panel.hidden = false;
        // Next frame so the transition actually runs from the hidden state.
        requestAnimationFrame(function () { panel.classList.add('is-open'); });
        if (btn) btn.setAttribute('aria-expanded', 'true');
        openName = name;
        if (mqDrawer.matches) {
          document.documentElement.classList.add('dtc-menu-open');
          inertBackground();
        }

        var focusTarget = panel.querySelector('[data-dtc-autofocus]');
        if (focusTarget) {
          // Let the panel paint before stealing focus (iOS keyboard jump).
          var t = setTimeout(function () { try { focusTarget.focus(); } catch (e) {} }, 40);
          timers.push(t);
        }
      }

      function close(returnFocus) {
        if (!openName) return;
        var panel = panels[openName];
        var btn = triggers[openName];
        openName = null;
        document.documentElement.classList.remove('dtc-menu-open');
        releaseBackground();
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (!panel) return;
        panel.classList.remove('is-open');
        if (DTC.reducedMotion) {
          panel.hidden = true;
        } else {
          var t = setTimeout(function () {
            if (!panel.classList.contains('is-open')) panel.hidden = true;
          }, 260);
          timers.push(t);
        }
        if (returnFocus && lastTrigger) { try { lastTrigger.focus(); } catch (e) {} }
        lastTrigger = null;
      }

      /* --- Triggers ----------------------------------------------------- */
      Object.keys(triggers).forEach(function (name) {
        offs.push(DTC.on(triggers[name], 'click', function (e) {
          e.preventDefault();
          if (openName === name) close(true); else open(name);
        }));
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-dtc-close]'), function (btn) {
        offs.push(DTC.on(btn, 'click', function () { close(true); }));
      });

      /* --- Click handling ----------------------------------------------- */
      offs.push(DTC.on(document, 'click', function (e) {
        if (!openName) return;
        var panel = panels[openName];
        var inPanel = panel && panel.contains(e.target);
        var onTrigger = e.target.closest && e.target.closest('[data-dtc-toggle]');
        if (onTrigger) return;                       // handled above
        if (!inPanel) { close(false); return; }      // outside click
        // Inside: the SHOP panel closes on any click; the search panel only
        // closes when a result link is followed (so typing still works).
        if (openName === 'shop' || (e.target.closest && e.target.closest('a'))) close(false);
      }));

      /* --- Keyboard ------------------------------------------------------ */
      offs.push(DTC.on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && openName) { e.stopPropagation(); close(true); }
      }));

      /* Tabbing out of the header closes the panel — dropdown only. The
         mobile drawer keeps focus instead (the background is inert). */
      offs.push(DTC.on(root, 'focusout', function (e) {
        if (!openName || mqDrawer.matches) return;
        var next = e.relatedTarget;
        if (next && root.contains(next)) return;
        if (!next) return;                            // clicks/blur to nothing
        close(false);
      }));

      /* --- Viewport ------------------------------------------------------ */
      offs.push(DTC.on(window, 'resize', function () {
        if (!openName) return;
        positionDrawer();
        if (!mqDrawer.matches) {
          document.documentElement.classList.remove('dtc-menu-open');
          releaseBackground();
        } else {
          inertBackground();
        }
      }, { passive: true }));

      offs.push(DTC.on(window, 'scroll', function () {
        if (openName && mqDrawer.matches) positionDrawer();
      }, { passive: true }));

      /* ------------------------------------------------------------------ *
       * Predictive search (progressive enhancement).
       * ------------------------------------------------------------------ */
      var searchPanel = panels.search;
      var input = searchPanel && searchPanel.querySelector('[data-dtc-search-input]');
      var results = searchPanel && searchPanel.querySelector('[data-dtc-search-results]');
      var predictive = searchPanel && searchPanel.getAttribute('data-predictive') === 'true';

      if (predictive && input && results) {
        var debounce = null;
        var controller = null;
        var base = (DTC.routes && DTC.routes.predictiveSearch) || '/search/suggest';

        offs.push(DTC.on(input, 'input', function () {
          clearTimeout(debounce);
          var q = input.value.trim();
          if (q.length < 2) { results.textContent = ''; return; }
          debounce = setTimeout(function () { suggest(q); }, 220);
        }));
        timers.push({ clear: function () { clearTimeout(debounce); } });

        var suggest = function (q) {
          if (controller && controller.abort) controller.abort();
          controller = ('AbortController' in window) ? new AbortController() : null;
          var url = base + '.json?q=' + encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=6' +
            '&resources[options][unavailable_products]=last';

          fetch(url, {
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined
          })
            .then(function (r) { return r.json(); })
            .then(function (data) {
              var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
              render(products, q);
            })
            .catch(function () { /* silent — the form still submits */ });
        };

        var render = function (products, q) {
          results.textContent = '';
          products = products.filter(function (p) {
            return (p.title || '').indexOf('Shipping Add-On') === -1;
          });
          if (!products.length) {
            var none = document.createElement('p');
            none.className = 'dtc-search__note';
            none.textContent = 'No matches for “' + q + '”';
            results.appendChild(none);
            return;
          }
          var list = document.createElement('div');
          list.className = 'dtc-search__list';
          products.forEach(function (p) {
            var a = document.createElement('a');
            a.className = 'dtc-search__item';
            a.href = p.url;

            if (p.image) {
              var img = document.createElement('img');
              img.className = 'dtc-search__thumb';
              img.src = p.image;
              img.alt = '';
              img.width = 44;
              img.height = 44;
              img.loading = 'lazy';
              a.appendChild(img);
            }

            var name = document.createElement('span');
            name.className = 'dtc-search__name';
            name.textContent = p.title;
            a.appendChild(name);

            if (p.price) {
              // suggest.json returns either bare cents ("2400") or an
              // already-formatted string, depending on the endpoint version.
              var price = document.createElement('span');
              price.className = 'dtc-search__price';
              price.textContent = (/^\d+$/.test(String(p.price)) && DTC.money)
                ? DTC.money(p.price)
                : String(p.price);
              a.appendChild(price);
            }

            list.appendChild(a);
          });
          results.appendChild(list);
        };
      }

      /* --- Cleanup ------------------------------------------------------- */
      return function cleanup() {
        offs.forEach(function (off) { off(); });
        timers.forEach(function (t) {
          if (t && t.clear) t.clear(); else clearTimeout(t);
        });
        document.documentElement.classList.remove('dtc-menu-open');
        releaseBackground();
        root.style.removeProperty('--hdr-bottom');
      };
    }
  });
}());
